import { deriveIso3166_2 } from './iso-3166-2';

/**
 * Minimal structural shape of a Google `GeocoderResult` — only the fields this util
 * reads. The real `google.maps.GeocoderResult` is structurally assignable, so callers
 * pass live results directly while this module stays free of the `google.maps` types
 * and is unit-testable with plain fixtures.
 */
export interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface MinimalGeocoderResult {
  address_components: GeocoderAddressComponent[];
  formatted_address: string;
  place_id: string;
}

/** The address-derived fields of a candidate site (lat/lng come from the caller). */
export interface GeocodedAddress {
  country: string;
  postalCode: string;
  adminRegion: string;
  iso3166_2: string;
  displayAddress: string;
  placeId: string;
}

function findComponent(
  components: GeocoderAddressComponent[],
  type: string,
): GeocoderAddressComponent | undefined {
  return components.find((component) => component.types.includes(type));
}

/** Map a geocoder result to candidate-site address fields. Missing parts → ''. */
export function mapGeocoderResult(
  result: MinimalGeocoderResult,
): GeocodedAddress {
  const components = result.address_components ?? [];
  const country = findComponent(components, 'country');
  const postal = findComponent(components, 'postal_code');
  const admin = findComponent(components, 'administrative_area_level_1');

  const countryCode = country?.short_name ?? '';
  const adminShort = admin?.short_name ?? '';
  const adminLong = admin?.long_name ?? '';

  return {
    country: countryCode,
    postalCode: postal?.long_name ?? '',
    adminRegion: adminLong,
    iso3166_2: deriveIso3166_2(countryCode, adminShort, adminLong),
    displayAddress: result.formatted_address ?? '',
    placeId: result.place_id ?? '',
  };
}
